import { LoadingOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Tag } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { type ReactNode, useCallback } from "react";
import { useIntl } from "react-intl";
import { NotificationStatuses } from "~enums/notificationStatuses.ts";
import { useDeleteNotification } from "~pages/Notifications/hooks/useDeleteNotification.ts";
import { useMarkAsReadNotification } from "~pages/Notifications/hooks/useMarkAsReadNotification.ts";
import type { Notification } from "~types/notification.ts";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

type Props = {
  notification: Notification;
  icon: string;
  customButtons?: Array<ReactNode>;
  children?: ReactNode;
};

const getNotificationColor = (status: string) => {
  switch (status) {
    case NotificationStatuses.Completed:
      return "success";
    case NotificationStatuses.New:
      return "default";
    case NotificationStatuses.Failed:
      return "error";
    case NotificationStatuses.InProgress:
      return "processing";
    case NotificationStatuses.Archived:
      return "default";
    default:
      return "default";
  }
};

export const NotificationItemBody = ({ notification, icon, customButtons = [], children }: Props) => {
  const timeAgo = dayjs.utc(notification.createdAtUtc).local().fromNow();
  const { formatMessage } = useIntl();

  const { mutateAsync: markAsRead } = useMarkAsReadNotification();

  const onMarkAsReadButtonClicked = useCallback(async (key: string) => await markAsRead(key), [markAsRead]);

  const { mutateAsync: deleteNotification } = useDeleteNotification();
  const [modal, contextHolder] = Modal.useModal();

  const onDelete = useCallback(async (notificationKey: string) => await deleteNotification(notificationKey), [deleteNotification]);

  const onDeleteButtonClicked = (notificationKey: string) => {
    modal.confirm({
      title: formatMessage({ id: "notification.delete" }),
      content: formatMessage({ id: "notification.delete.confirm" }),
      okText: formatMessage({ id: "delete" }),
      okButtonProps: { loading: false },
      onOk: () => onDelete(notificationKey),
    });
  };

  return (
    <div
      style={{
        padding: "10px",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {contextHolder}
      <div style={{ marginRight: "10px" }}>
        <i className={`fas ${icon} mr-2`} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div>
          <span style={{ fontWeight: "bolder" }}>{notification.name || notification.key}</span>
          <Tag style={{ marginLeft: 10 }} color={getNotificationColor(notification.status)}>
            {notification.status}
          </Tag>
        </div>
        {notification.status !== NotificationStatuses.Failed && <div style={{ fontWeight: 500 }}>{notification.description}</div>}
        {notification.status === NotificationStatuses.Failed && (
          <div style={{ color: "red", maxWidth: "600px", wordWrap: "break-word" }}>{notification.error}</div>
        )}
        {children}
        <div style={{ fontWeight: 700 }}>{timeAgo}</div>
      </div>
      <div style={{ alignItems: "center", display: "flex", gap: "10px" }}>
        {customButtons.length > 0 && customButtons?.map((Button) => Button)}
        {notification.unread && (
          <Button type="primary" onClick={() => onMarkAsReadButtonClicked(notification.key)}>
            {formatMessage({ id: "mark.as.read" })}
          </Button>
        )}
        {[NotificationStatuses.Completed, NotificationStatuses.Failed].includes(notification.status as NotificationStatuses) && (
          <Button type="primary" onClick={() => onDeleteButtonClicked(notification.key)}>
            {formatMessage({ id: "delete" })}
          </Button>
        )}
        {notification.status === NotificationStatuses.InProgress && <Spin indicator={<LoadingOutlined spin />} />}
      </div>
    </div>
  );
};
